// HR admins choose what Ask HR reads: libraries and folders on Ask HR's SharePoint sites (the HR
// site, plus any IT has allowed), and websites. Top: the current list (change who each applies to,
// or remove). Then: add a website by address, and browse a site to add a library or folder. The API
// re-checks everything: role, that the site is allowed, overlap, and that a website is public and
// reachable.

import { useEffect, useState } from "react";
import type { FolderListing, FolderRef, Library, Site } from "@ask-hr/core/graph";
import {
  APPLICABILITY_LABELS,
  ROOT,
  coveringSource,
  type ApplicabilityValue,
  type SharePointSource,
} from "@ask-hr/core/sources";
import { ApiError, api } from "../api.js";

const APPLICABILITY = Object.entries(APPLICABILITY_LABELS) as [ApplicabilityValue, string][];

type Target = Pick<FolderRef, "driveId" | "driveName" | "itemId" | "name" | "path"> & { siteId?: string };
type SiteInfo = Site & { available: boolean };

export function SourcesPage() {
  const [sources, setSources] = useState<SharePointSource[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [status, setStatus] = useState<string>("");
  const [applyTo, setApplyTo] = useState<ApplicabilityValue>("all");
  const [busy, setBusy] = useState<string | null>(null);
  const [replacing, setReplacing] = useState<{ target: Target; sources: SharePointSource[] } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [sites, setSites] = useState<SiteInfo[]>([]);

  const fetchSources = () => api<{ sources: SharePointSource[] }>("GET", "/api/admin/sources").then((r) => r.sources);

  const reload = async () => {
    try {
      setSources(await fetchSources());
      setError(null);
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    let live = true;
    fetchSources().then(
      (s) => live && setSources(s),
      (e: unknown) => live && setError(e),
    );
    // Errors show in the browser below, which asks for the same sites' libraries.
    api<{ sites: SiteInfo[] }>("GET", "/api/admin/sharepoint/sites").then(
      (r) => live && setSites(r.sites),
      () => {},
    );
    return () => {
      live = false;
    };
  }, []);

  // A source's site, named when it isn't the HR site (the first; sources from before there were
  // several sites have no siteId and are on it).
  const otherSite = (s: SharePointSource) =>
    s.kind !== "web" && s.siteId && s.siteId !== sites[0]?.id ? sites.find((x) => x.id === s.siteId)?.name ?? "Another site" : null;

  async function add(target: Target, replace = false) {
    const key = `${target.driveId}~${target.itemId}`;
    setBusy(key);
    setStatus("");
    try {
      await api("POST", "/api/admin/sources", {
        siteId: target.siteId,
        driveId: target.driveId,
        itemId: target.itemId,
        applicability: applyTo,
        replace,
      });
      setReplacing(null);
      setStatus(`Added ${label(target)}.`);
      await reload();
    } catch (e) {
      if (e instanceof ApiError && e.body.error === "would_replace") {
        setReplacing({ target, sources: e.body.sources as SharePointSource[] });
      } else if (e instanceof ApiError && e.body.error === "already_included") {
        setStatus(`${label(target)} is already included.`);
        await reload();
      } else {
        setError(e);
      }
    } finally {
      setBusy(null);
    }
  }

  async function addWebsite(url: string): Promise<boolean> {
    setBusy("web");
    setStatus("");
    try {
      const r = await api<{ source: SharePointSource; found: number }>("POST", "/api/admin/sources", { url, applicability: applyTo });
      setStatus(
        r.source.feed === "lever"
          ? `Added ${r.source.driveName}: ${r.found} open ${r.found === 1 ? "job" : "jobs"}. They go live after the next sync, without review.`
          : `Added ${r.source.driveName}. Its text waits for review on the Documents tab after the next sync.`,
      );
      await reload();
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.body.error === "already_included") setStatus("That website is already on the list.");
      else setError(e);
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function setApplicability(s: SharePointSource, applicability: ApplicabilityValue) {
    setBusy(s.id);
    try {
      await api("PATCH", `/api/admin/sources/${encodeURIComponent(s.id)}`, { applicability });
      setStatus(`${label(s)} now applies to: ${APPLICABILITY_LABELS[applicability]}.`);
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function remove(s: SharePointSource) {
    setBusy(s.id);
    try {
      await api("DELETE", `/api/admin/sources/${encodeURIComponent(s.id)}`);
      setConfirmRemove(null);
      setStatus(`Removed ${label(s)}.`);
      await reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <section className="panel" aria-labelledby="sources-h">
        <h2 id="sources-h">Sources</h2>
        <p className="sub">
          Ask HR reads only what's listed here: libraries and folders on its SharePoint sites (folders inside them
          are included), and websites. New and changed documents wait for HR review before Ask HR uses them in
          answers, except Lever job postings, which go live on each hourly sync. A document can apply to a different
          group than its source: set that on the Documents tab.
        </p>

        {error ? <ErrorNotice error={error} /> : null}
        <p className="status" role="status">
          {status}
        </p>

        {sources === null ? (
          error ? null : <p className="sub">Loading…</p>
        ) : sources.length === 0 ? (
          <p className="empty">No sources yet. Choose a library or folder below.</p>
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Source</th>
                  <th scope="col">Folder or address</th>
                  <th scope="col">Applies to</th>
                  <th scope="col">Added</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.driveName}
                      {otherSite(s) ? <div className="muted small">{otherSite(s)} site</div> : null}
                    </td>
                    <td>
                      {s.kind === "web" ? (
                        <>
                          <span className="pill">{s.feed === "lever" ? "Jobs · no review" : "Website"}</span>{" "}
                          <span className="muted small">{hostPath(s.webUrl)}</span>
                        </>
                      ) : s.path === "/" ? (
                        <span className="muted">Whole library</span>
                      ) : (
                        s.path
                      )}
                    </td>
                    <td>
                      <select
                        aria-label={`Who ${label(s)} applies to`}
                        value={s.applicability}
                        disabled={busy === s.id}
                        onChange={(e) => void setApplicability(s, e.target.value as ApplicabilityValue)}
                      >
                        {APPLICABILITY.map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="muted" title={s.addedBy}>
                      {new Date(s.addedAt).toLocaleDateString()} · {s.addedBy}
                    </td>
                    <td>
                      <div className="actions">
                        <a href={s.webUrl} target="_blank" rel="noreferrer">
                          {s.kind === "web" ? "Open website" : "Open in SharePoint"}
                        </a>
                        {confirmRemove === s.id ? (
                          <>
                            <button type="button" className="danger" disabled={busy === s.id} onClick={() => void remove(s)}>
                              Remove {label(s)}
                            </button>
                            <button type="button" onClick={() => setConfirmRemove(null)}>
                              Keep
                            </button>
                          </>
                        ) : (
                          <button type="button" onClick={() => setConfirmRemove(s.id)}>
                            Remove…
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {replacing ? (
        <section className="panel notice" role="alert">
          <p>
            <strong>{label(replacing.target)}</strong> contains {replacing.sources.length === 1 ? "a source" : "sources"}{" "}
            already on the list. Adding it replaces{" "}
            {replacing.sources.map((s, i) => (
              <span key={s.id}>
                {i ? ", " : ""}
                {label(s)} ({APPLICABILITY_LABELS[s.applicability]})
              </span>
            ))}
            , and everything in it will apply to: {APPLICABILITY_LABELS[applyTo]}.
          </p>
          <div className="actions start">
            <button type="button" className="primary" disabled={busy !== null} onClick={() => void add(replacing.target, true)}>
              Replace and add
            </button>
            <button type="button" onClick={() => setReplacing(null)}>
              Cancel
            </button>
          </div>
        </section>
      ) : null}

      <WebsiteForm busy={busy !== null} applyTo={applyTo} onApplyTo={setApplyTo} onAdd={addWebsite} />

      <Browser
        sites={sites}
        sources={sources ?? []}
        applyTo={applyTo}
        onApplyTo={setApplyTo}
        busy={busy}
        onAdd={(t) => void add(t)}
      />
    </>
  );
}

type Crumb = { driveId: string; driveName: string; itemId: string; name: string };

function Browser({
  sites,
  sources,
  applyTo,
  onApplyTo,
  busy,
  onAdd,
}: {
  sites: SiteInfo[];
  sources: SharePointSource[];
  applyTo: ApplicabilityValue;
  onApplyTo: (a: ApplicabilityValue) => void;
  busy: string | null;
  onAdd: (t: Target) => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const [trail, setTrail] = useState<Crumb[]>([]);
  const [libraries, setLibraries] = useState<{ siteId: string; list: Library[] } | null>(null);
  const [listing, setListing] = useState<FolderListing | null>(null);
  const [error, setError] = useState<unknown>(null);
  const here = trail.at(-1);
  // The site being browsed: the one picked, else the HR site. Empty until the sites load: the API
  // then uses the HR site.
  const siteId = chosen ?? sites[0]?.id ?? "";
  const site = sites.find((s) => s.id === siteId);
  const siteQuery = siteId ? `siteId=${encodeURIComponent(siteId)}&` : "";

  useEffect(() => {
    let live = true;
    const load = here
      ? api<FolderListing>(
          "GET",
          `/api/admin/sharepoint/folder?${siteQuery}driveId=${encodeURIComponent(here.driveId)}&itemId=${encodeURIComponent(here.itemId)}`,
        ).then((l) => live && (setListing(l), setError(null)))
      : api<{ libraries: Library[] }>("GET", `/api/admin/sharepoint/libraries${siteQuery ? `?${siteQuery.slice(0, -1)}` : ""}`).then(
          (r) => live && (setLibraries({ siteId, list: r.libraries }), setError(null)),
        );
    load.catch((e: unknown) => live && setError(e));
    return () => {
      live = false;
    };
  }, [here, siteId, siteQuery]);

  const libs = libraries && libraries.siteId === siteId ? libraries.list : null;
  const shown = here && listing?.folder.driveId === here.driveId && listing.folder.itemId === here.itemId ? listing : null;
  const loading = here ? !shown : !libs;
  const several = sites.length > 1;
  const siteName = site?.name ?? "HR site";

  const pickSite = (id: string) => {
    setChosen(id);
    setTrail([]);
    setError(null);
  };

  const status = (driveId: string, path: string, target: Target) => {
    const by = coveringSource(sources, driveId, path);
    if (by) {
      return (
        <span className="pill" title={by.path === path ? "On the list" : `Included through ${label(by)}`}>
          Included
        </span>
      );
    }
    const key = `${target.driveId}~${target.itemId}`;
    return (
      <button
        type="button"
        disabled={busy !== null}
        aria-label={`Add ${label(target)}`}
        onClick={() => onAdd({ ...target, ...(siteId ? { siteId } : {}) })}
      >
        {busy === key ? "Adding…" : "Add"}
      </button>
    );
  };

  return (
    <section className="panel" aria-labelledby="browse-h">
      <h2 id="browse-h">{several ? "Browse SharePoint" : "Browse the HR site"}</h2>
      <div className="toolbar">
        {several ? (
          <label>
            Site{" "}
            <select value={siteId} onChange={(e) => pickSite(e.target.value)}>
              {sites.map((s) => (
                <option key={s.id} value={s.id} disabled={!s.available}>
                  {s.name}
                  {s.available ? "" : " (not shared with Ask HR yet)"}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          New sources apply to{" "}
          <select value={applyTo} onChange={(e) => onApplyTo(e.target.value as ApplicabilityValue)}>
            {APPLICABILITY.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>

      <nav className="crumbs" aria-label="Folder path">
        <button type="button" aria-current={trail.length === 0 ? "page" : undefined} onClick={() => setTrail([])}>
          {several ? siteName : "HR site"}
        </button>
        {trail.map((c, i) => (
          <span key={`${c.driveId}~${c.itemId}`}>
            <span aria-hidden="true">›</span>{" "}
            <button
              type="button"
              aria-current={i === trail.length - 1 ? "page" : undefined}
              onClick={() => setTrail(trail.slice(0, i + 1))}
            >
              {c.name}
            </button>
          </span>
        ))}
      </nav>

      {error ? <ErrorNotice error={error} /> : null}

      {loading && !error ? <p className="sub">Loading…</p> : null}

      {!here && libs && !error ? (
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Library</th>
                <th scope="col">
                  <span className="sr-only">Status</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {libs.length === 0 ? (
                <tr>
                  <td colSpan={2} className="muted">
                    Ask HR can't see any libraries on {several ? `the ${siteName} site` : "the HR site"}.
                  </td>
                </tr>
              ) : (
                libs.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <button
                        type="button"
                        className="linkbtn"
                        onClick={() => setTrail([{ driveId: l.id, driveName: l.name, itemId: ROOT, name: l.name }])}
                      >
                        {l.name}
                      </button>
                    </td>
                    <td className="right">
                      {status(l.id, "/", { driveId: l.id, driveName: l.name, itemId: ROOT, name: l.name, path: "/" })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {here && shown && !error ? (
        <>
          <div className="toolbar">
            <span>
              <strong>{shown.folder.itemId === ROOT ? `${shown.folder.name} (whole library)` : shown.folder.name}</strong>
              <span className="muted">
                {" "}
                · {shown.files.supported} readable {shown.files.supported === 1 ? "file" : "files"} (PDF, Word)
                {shown.files.other ? `, ${shown.files.other} other` : ""} here
              </span>
            </span>
            {status(shown.folder.driveId, shown.folder.path, shown.folder)}
            <a href={shown.folder.webUrl} target="_blank" rel="noreferrer">
              Open in SharePoint
            </a>
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Folder</th>
                  <th scope="col">Items</th>
                  <th scope="col">
                    <span className="sr-only">Status</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.folders.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="muted">
                      No folders inside.
                    </td>
                  </tr>
                ) : (
                  shown.folders.map((f) => (
                    <tr key={f.itemId}>
                      <td>
                        <button
                          type="button"
                          className="linkbtn"
                          onClick={() =>
                            setTrail([...trail, { driveId: f.driveId, driveName: f.driveName, itemId: f.itemId, name: f.name }])
                          }
                        >
                          {f.name}
                        </button>
                      </td>
                      <td className="muted">{f.childCount}</td>
                      <td className="right">{status(f.driveId, f.path, f)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}

function WebsiteForm({
  busy,
  applyTo,
  onApplyTo,
  onAdd,
}: {
  busy: boolean;
  applyTo: ApplicabilityValue;
  onApplyTo: (a: ApplicabilityValue) => void;
  onAdd: (url: string) => Promise<boolean>;
}) {
  const [url, setUrl] = useState("");
  return (
    <section className="panel" aria-labelledby="web-h">
      <h2 id="web-h">Add a website</h2>
      <p className="sub">
        A public https page, read as one document (links on it aren't followed), or a Lever jobs page such as
        https://jobs.lever.co/novara, where each open job becomes its own document. Pages that need sign-in or only
        draw their text with JavaScript can't be read.
      </p>
      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          void onAdd(url.trim()).then((ok) => ok && setUrl(""));
        }}
      >
        <label className="grow">
          <span className="sr-only">Website address</span>
          <input type="url" required placeholder="https://" value={url} onChange={(e) => setUrl(e.target.value)} />
        </label>
        <label>
          Applies to{" "}
          <select value={applyTo} onChange={(e) => onApplyTo(e.target.value as ApplicabilityValue)}>
            {APPLICABILITY.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="primary" disabled={busy || !url.trim()}>
          {busy ? "Checking…" : "Add website"}
        </button>
      </form>
    </section>
  );
}

function hostPath(url: string): string {
  try {
    const u = new URL(url);
    return `${u.hostname}${u.pathname === "/" ? "" : u.pathname}`;
  } catch {
    return url;
  }
}

function label(s: Pick<Target, "driveName" | "path" | "name"> | SharePointSource): string {
  return s.path === "/" ? s.driveName : `${s.driveName}${s.path}`;
}

function ErrorNotice({ error }: { error: unknown }) {
  let text = "Something went wrong. Try again, and tell IT if it keeps happening.";
  if (error instanceof ApiError) {
    if (error.body.error === "sharepoint_not_configured") {
      text = "The SharePoint connection isn't set up yet. IT needs to add the Ask HR backend's credentials.";
    } else if (error.body.error === "not_on_hr_site") {
      text = "That folder isn't on that SharePoint site any more. It may have been moved or deleted.";
    } else if (error.status === 403) {
      text = "Only HR admins (the HR.Admin role) can manage sources.";
    } else if (error.body.error === "web_unreachable") {
      text =
        typeof error.body.status === "number" && error.body.status
          ? `That website answered with an error (HTTP ${error.body.status}). Check the address.`
          : error.body.code === "web_type"
            ? "That address isn't a web page or a Lever jobs feed Ask HR can read."
            : error.body.code === "web_blocked"
              ? "That address redirects somewhere Ask HR won't go (an internal or non-https address)."
              : "Ask HR couldn't reach that website. Check the address and try again.";
    } else if (error.status === 400 && typeof error.body.detail === "string") {
      text = error.body.detail;
    } else if (error.body.error === "sharepoint_error") {
      text = "SharePoint returned an error. Try again in a minute.";
    }
  }
  return (
    <p className="notice" role="alert">
      {text}
    </p>
  );
}
